import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import LeaveAdjustmentModal from './LeaveAdjustmentModal';
import { formatDate } from '../util/formatDate';
import UserAvatar from '../util/UserAvatar';
import { roundRuleLabel } from './roundingLabel';

interface Props {
  onClose: () => void;
  row: any | null;            // { user, balances[] }
  year: number;
  baseUrl: string;
  canAdjust: boolean;
  onChanged: () => void;      // refetch сводной таблицы
  staticDateCompany?: string;
  unitMode?: 'auto' | 'days' | 'hours';
  onOpenProfile?: (userId: number) => void;
}

const r1 = (v: number | null | undefined) => (v === null || v === undefined ? 0 : Math.round(v * 10) / 10);

const cRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const cK: React.CSSProperties = { color: 'var(--text-color-calendar-week)' };
const cNum: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };
const cTot: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-input)', marginTop: 2, paddingTop: 7 };

const HrLeaveDetailPanel: React.FC<Props> = ({ onClose, row, year, baseUrl, canAdjust, onChanged, staticDateCompany, unitMode = 'auto', onOpenProfile }) => {
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [absences, setAbsences] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [view, setView] = useState<'main' | 'history' | 'adjust'>('main');
  const [returnView, setReturnView] = useState<'main' | 'history'>('main');
  const [histSearch, setHistSearch] = useState('');
  const [openTypes, setOpenTypes] = useState<Record<number, boolean>>({});
  const toggleType = (id: number) => setOpenTypes(o => ({ ...o, [id]: !o[id] }));
  const [openBase, setOpenBase] = useState<Record<number, boolean>>({});
  const toggleBase = (id: number) => setOpenBase(o => ({ ...o, [id]: !o[id] }));

  const user = row?.user;
  const dS = t('DShort') as string;
  const hS = t('HShort') as string;
  const eff = (b: any) => unitMode === 'auto' ? (b?.patternUnit === 'hours' ? 'hours' : 'days') : unitMode;
  const val = (b: any, days: number | null | undefined, hours: number | null | undefined) =>
    eff(b) === 'hours' ? `${r1(hours)} ${hS}` : `${days ?? 0} ${dS}`;

  const loadAdjustments = useCallback(async () => {
    if (!user) return;
    try { const { data } = await axios.get(`hr-leave-adjustment?userId=${user.id}&year=${year}`); setAdjustments(Array.isArray(data) ? data : []); } catch { /* interceptor */ }
  }, [user, year]);
  const loadAbsences = useCallback(async () => {
    if (!user) return;
    try { const { data } = await axios.get(`hr-absence?userId=${user.id}`); setAbsences(Array.isArray(data) ? data : []); } catch { /* interceptor */ }
  }, [user]);
  const loadTypes = useCallback(async () => {
    try {
      const { data } = await axios.get('hr-absence-type');
      setTypes((Array.isArray(data) ? data : []).filter((tp: any) => tp.category === 'leave').sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? '')));
    } catch { /* interceptor */ }
  }, []);

  useEffect(() => { if (row) { loadAdjustments(); loadTypes(); loadAbsences(); } }, [row, loadAdjustments, loadTypes, loadAbsences]);
  useEffect(() => { setView('main'); }, [user?.id]);

  const afterChange = () => { loadAdjustments(); loadAbsences(); onChanged(); };

  const balances = row?.balances ?? [];
  const primary = useMemo(() => balances.find((b: any) => b.isDefault) ?? balances[0] ?? null, [balances]);

  // По умолчанию раскрыт основной тип (сбрасывается при смене сотрудника)
  useEffect(() => { setOpenTypes(primary ? { [primary.typeId]: true } : {}); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const serviceLength = useMemo(() => {
    const hire = user?.contract_start_date;
    if (!hire) return null;
    const h = new Date(hire); const now = new Date();
    if (isNaN(h.getTime()) || h > now) return null;
    let years = now.getFullYear() - h.getFullYear();
    let months = now.getMonth() - h.getMonth();
    let days = now.getDate() - h.getDate();
    if (days < 0) { months--; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
    if (months < 0) { years--; months += 12; }
    return { years, months, days };
  }, [user]);

  // Единый реестр движения: взятые отпуска (read-only) + корректировки (edit/delete) — без иконок
  const history = useMemo(() => {
    const winStart: string | undefined = row?.balances?.[0]?.leaveYearStart;
    const inWindow = (d?: string) => {
      if (!d) return false;
      if (!winStart) return new Date(d).getFullYear() === year;
      const s = new Date(winStart + 'T00:00:00'); const e = new Date(s); e.setFullYear(e.getFullYear() + 1);
      const dd = new Date(d + 'T00:00:00'); return dd >= s && dd < e;
    };
    const tName = (id: number) => types.find(x => x.id === id)?.name
      ?? (row?.balances ?? []).find((b: any) => b.typeId === id)?.typeName
      ?? (t('HrLeave', 'Leave') as string);
    const avgOf = (id: number) => (row?.balances ?? []).find((b: any) => b.typeId === id)?.avgHoursPerDay ?? 8;
    const colorOf = (id: number) => (row?.balances ?? []).find((b: any) => b.typeId === id)?.typeColor
      ?? types.find((tp: any) => tp.id === id)?.color ?? 'var(--text-color-calendar)';
    const items: any[] = [];
    adjustments.forEach(a => items.push({
      id: `adj-${a.id}`, date: (a.createdAt || '').slice(0, 10),
      typeName: tName(a.typeId), color: colorOf(a.typeId),
      badge: t('HrAdjKind_' + a.kind) as string, badgeKind: 'adj',
      meta: a.reason || '',
      hours: a.unit === 'hours' ? a.amount : a.amount * avgOf(a.typeId),
      days: a.unit === 'days' ? a.amount : (avgOf(a.typeId) ? a.amount / avgOf(a.typeId) : 0),
      isAdj: true, raw: a,
    }));
    absences
      .filter(x => x.category === 'leave' && ['approved', 'pending', 'recorded'].includes(x.status) && inWindow(x.startDate))
      .forEach(x => items.push({
        id: `abs-${x.id}`, date: (x.createdAt || x.startDate || '').slice(0, 10), leave: true,
        typeName: tName(x.typeId), color: colorOf(x.typeId),
        badge: t('HrStatus_' + x.status) as string, badgeKind: x.status,
        meta: `${formatDate(x.startDate, staticDateCompany)} → ${formatDate(x.endDate, staticDateCompany)}`,
        hours: -(x.hours ?? 0), days: -(x.days ?? 0), isAdj: false,
      }));
    items.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return items;
  }, [adjustments, absences, row, year, types, staticDateCompany]);

  const filteredHistory = useMemo(() => {
    const q = histSearch.trim().toLowerCase();
    if (!q) return history;
    return history.filter(h => `${h.typeName} ${h.badge} ${h.meta}`.toLowerCase().includes(q));
  }, [history, histSearch]);

  // Стиль бейджа статуса/вида для таймлайна
  const badgeStyle = (kind: string): React.CSSProperties => {
    const base: React.CSSProperties = { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' };
    if (kind === 'approved' || kind === 'recorded') return { ...base, background: 'rgba(22,163,74,.14)', color: 'var(--color-success)' };
    if (kind === 'pending') return { ...base, background: 'rgba(217,119,6,.15)', color: '#d97706' };
    if (kind === 'rejected' || kind === 'cancelled') return { ...base, background: 'rgba(239,68,68,.14)', color: 'var(--color-danger)' };
    if (kind === 'adj') return { ...base, background: 'rgba(124,58,237,.14)', color: '#7c3aed' };
    return { ...base, background: 'var(--btn-user-in)', color: 'var(--text-color-calendar)' };
  };

  if (!row) return null;

  // ── Экран истории ─────────────────────────────────────────────
  if (view === 'history') {
    return (
      <div className="ts-card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: 'var(--border-input)' }}>
          <button type="button" className="dt-btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }} onClick={() => setView('main')}>← {t('Back', 'Back')}</button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('HrAbsenceHistory')}</div>
            <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.first_name} {user?.last_name} · {year}</div>
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 800, color: 'var(--text-color-calendar)', background: 'var(--btn-user-in)', border: 'var(--border-input)', borderRadius: 999, padding: '3px 10px' }}>{history.length}</span>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', borderBottom: 'var(--border-input)' }}>
          <input className="timesheet-edit-input" style={{ flex: 1 }} placeholder={t('Search') as string} value={histSearch} onChange={e => setHistSearch(e.target.value)} />
          {canAdjust && (
            <button type="button" className="dt-btn-ghost" style={{ whiteSpace: 'nowrap' }} onClick={() => { setEditing(null); setReturnView('history'); setView('adjust'); }}>+ {t('HrAdjustment')}</button>
          )}
        </div>

        <div className="thin-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '12px 16px 16px' }}>
          {filteredHistory.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 12 }}>{t('Empty')}</div>
          ) : filteredHistory.map((h: any, i: number) => (
            <div key={h.id} style={{ position: 'relative', padding: '0 0 15px 26px' }}>
              {i < filteredHistory.length - 1 && <span style={{ position: 'absolute', left: 6, top: 16, bottom: -3, width: 2, background: 'var(--border-input)' }} />}
              <span style={{ position: 'absolute', left: 0, top: 3, width: 13, height: 13, borderRadius: '50%', border: '3px solid var(--bg-form)', background: h.color, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 10.5, color: 'var(--text-color-calendar-week)', fontVariantNumeric: 'tabular-nums' }}>{formatDate(h.date, staticDateCompany)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>{h.typeName}</span>
                <span style={badgeStyle(h.badgeKind)}>{h.badge}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                {h.meta ? `${h.meta} · ` : ''}
                <span style={{ color: h.days >= 0 ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 700 }}>{h.days >= 0 ? '+' : ''}{r1(h.days)} {dS} / {h.hours >= 0 ? '+' : ''}{r1(h.hours)} {hS}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Экран корректировки (вместо всплывающего окна) ─────────────
  if (view === 'adjust' && user) {
    return (
      <div className="ts-card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: 'var(--border-input)' }}>
          <button type="button" className="dt-btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }} onClick={() => setView(returnView)}>← {t('Back', 'Back')}</button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('HrAdjustment')}</div>
            <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.first_name} {user.last_name} · {year}</div>
          </div>
        </div>
        <LeaveAdjustmentModal embedded open onClose={() => setView(returnView)} onSaved={afterChange}
          userId={user.id} userName={`${user.first_name} ${user.last_name}`} year={year} types={types} editing={editing} />
      </div>
    );
  }

  // ── Основной экран ────────────────────────────────────────────
  return (
    <div className="ts-card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: 'var(--border-input)' }}>
        <UserAvatar user={user ?? {}} baseUrl={baseUrl} size={42} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.first_name} {user?.last_name}</div>
          {serviceLength
            ? <div style={{ fontSize: 11.5, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('HrServiceLength', 'Length of service')}: {serviceLength.years}{t('HrYearsShort', 'y')} {serviceLength.months}{t('HrMonthsShort', 'm')} {serviceLength.days}{dS}</div>
            : (user?.job_title ? <div style={{ fontSize: 11.5, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.job_title}</div> : null)}
        </div>
        {onOpenProfile && user?.id && (
          <button
            type="button"
            className="dt-btn-ghost"
            style={{ marginLeft: 'auto', fontSize: 11, whiteSpace: 'nowrap' }}
            onClick={() => onOpenProfile(user.id)}
          >
            {t('OpenProfile', 'Open profile')}
          </button>
        )}
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-color-calendar)', background: 'var(--btn-user-in)', border: 'var(--border-input)', borderRadius: 999, padding: '3px 10px', fontVariantNumeric: 'tabular-nums' }}>{year}</span>
        <span style={{ cursor: 'pointer', opacity: 0.45, fontSize: 16, marginLeft: 4 }} onClick={onClose}>✕</span>
      </div>

      {/* Body */}
      <div className="thin-scroll" style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* KPI по типам — остатки */}
        {balances.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px,1fr))', gap: 10 }}>
            {balances.map((b: any) => (
              <div key={b.typeId} style={{ border: 'var(--border-input)', borderRadius: 12, padding: '11px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: b.typeColor || 'var(--text-color-calendar)' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.typeName}</span>
                </div>
                {b.entitlementDays === null
                  ? <div style={{ fontSize: 21, fontWeight: 800, color: 'var(--text-color-calendar)', lineHeight: 1 }}>∞</div>
                  : <div style={{ fontSize: 21, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: (b.remainingDays ?? 0) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>{val(b, b.remainingDays, b.remainingHours)}</div>}
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-color-calendar-week)' }}>{t('HrRemaining')}</div>
              </div>
            ))}
          </div>
        )}

        {/* Аккордеоны по типам с расчётом */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {balances.map((b: any) => {
            const open = !!openTypes[b.typeId];
            const minus = (s: string) => <span style={{ color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums' }}>−{s}</span>;
            return (
              <div key={b.typeId} style={{ border: 'var(--border-input)', borderRadius: 12, overflow: 'hidden' }}>
                <div onClick={() => toggleType(b.typeId)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '11px 13px', cursor: 'pointer', userSelect: 'none', background: 'rgba(var(--shodow),.02)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: b.typeColor || 'var(--text-color-calendar)' }} />
                  <span style={{ fontWeight: 800, fontSize: 13, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.typeName}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-color-calendar)', fontVariantNumeric: 'tabular-nums' }}>{b.entitlementDays === null ? '∞' : val(b, b.remainingDays, b.remainingHours)}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .2s' }}>▾</span>
                </div>
                {open && (
                  <div style={{ padding: '11px 13px', borderTop: '1px solid var(--border-input-one)', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5 }}>
                    {b.entitlementDays === null ? (
                      <>
                        <div style={cRow}><span style={cK}>{t('HrEntitlement')}</span><b>∞</b></div>
                        <div style={cRow}><span style={cK}>{t('HrUsed')}</span><b>{minus(val(b, b.usedDays, b.usedHours))}</b></div>
                        <div style={cRow}><span style={cK}>{t('HrPending')}</span><b>{minus(val(b, b.pendingDays, b.pendingHours))}</b></div>
                      </>
                    ) : (
                      <>
                        {(() => {
                          const hasBreakdown = (b.baseBreakdown?.patternCount ?? 0) >= 1;
                          const multiPattern = (b.baseBreakdown?.patternCount ?? 0) > 1;
                          const baseOpen = !!openBase[b.typeId];
                          return (
                            <>
                              <div
                                style={{ ...cRow, cursor: hasBreakdown ? 'pointer' : 'default', userSelect: 'none' }}
                                onClick={hasBreakdown ? () => toggleBase(b.typeId) : undefined}
                              >
                                <span style={{ ...cK, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  {t('HrCalcBase', 'Base (per year)')}
                                  {multiPattern && (
                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '0 5px', borderRadius: 20, background: 'rgba(37,99,235,.12)', color: '#2563eb' }}>{b.baseBreakdown.patternCount} {t('HrPatternsWord', 'patterns')}</span>
                                  )}
                                  {hasBreakdown && <span style={{ fontSize: 9, color: 'var(--text-color-calendar-week)', transform: baseOpen ? 'none' : 'rotate(-90deg)', transition: 'transform .2s' }}>▾</span>}
                                </span>
                                {!(hasBreakdown && baseOpen) && <b style={cNum}>{val(b, b.entitlementBaseDays, b.entitlementBaseHours)}</b>}
                              </div>
                              {hasBreakdown && baseOpen && (
                                <div style={{ margin: '1px 0 4px', paddingLeft: 10, borderLeft: '2px solid var(--border-input)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {b.baseBreakdown.segments.map((s: any, i: number) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-color-calendar-week)', padding: '3px 0' }}>
                                      <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
                                        <span style={{ color: 'var(--text-color-calendar)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.patternName || '—'}</span>
                                        <span style={{ opacity: .85 }}>{formatDate(s.start, staticDateCompany)} – {formatDate(s.end, staticDateCompany)}</span>
                                      </span>
                                      <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{s.days}/{b.baseBreakdown.totalWindowDays}{dS} × {s.hoursPerWeek}{hS} = {s.contribution}{hS}</span>
                                    </div>
                                  ))}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, fontWeight: 800, borderTop: '1px solid var(--border-input-one)', marginTop: 2, paddingTop: 3 }}>
                                    <span>{t('HrWeightedPerWeek', 'Weighted h/week')}</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{b.baseBreakdown.weightedHoursPerWeek}{hS}</span>
                                  </div>
                                  {/* Формула → сырое (неокруглённое) значение */}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 10.5, color: 'var(--text-color-calendar-week)', fontStyle: 'italic' }}>
                                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {b.baseBreakdown.weeks} {t('HrWeeksWord', 'weeks')} × {b.baseBreakdown.weightedHoursPerWeek}{hS} = {b.baseBreakdown.baseHours}{hS} ≈
                                    </span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{val(b, b.baseBreakdown.baseDays, b.baseBreakdown.baseHours)}</span>
                                  </div>
                                  {/* Округление: правило + итог */}
                                  {(() => {
                                    const ru = b.baseBreakdown.roundingUnit === 'hours' ? t('HrUnitHour', 'hour') : t('HrUnitDay', 'day');
                                    const rlab = roundRuleLabel(b.baseBreakdown.roundingStep ?? 1, b.baseBreakdown.roundingThreshold ?? 0.5, ru, t);
                                    return (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 3, borderTop: '1px solid var(--border-input-one)', paddingTop: 4 }}>
                                        <span style={{ fontSize: 11, color: 'var(--text-color-calendar)', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {b.baseBreakdown.roundingApplied ? `${t('HrRoundedLabel', 'Rounded')}: ${rlab}` : t('HrNoRounding', 'No rounding')}
                                        </span>
                                        <b style={cNum}>{val(b, b.entitlementBaseDays, b.entitlementBaseHours)}</b>
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {b.proRataFactor < 1 && <div style={cRow}><span style={cK}>{t('HrProRata', 'Part year')}</span><b style={cNum}>× {b.proRataFactor}</b></div>}
                        {b.serviceBandHours > 0 && <div style={cRow}><span style={cK}>{t('HrCalcService', 'Long Service Bonus')}</span><b style={{ ...cNum, color: 'var(--color-success)' }}>+{val(b, b.serviceBandDays, b.serviceBandHours)}</b></div>}
                        {b.adjustmentsHours !== 0 && <div style={cRow}><span style={cK}>{t('HrAdjustments')}</span><b style={{ ...cNum, color: b.adjustmentsHours >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>{b.adjustmentsHours >= 0 ? '+' : ''}{val(b, b.adjustmentsDays, b.adjustmentsHours)}</b></div>}
                        <div style={cTot}><span style={{ fontWeight: 800 }}>{t('HrEntitlement')}</span><b style={cNum}>{val(b, b.entitlementDays, b.entitlementHours)}</b></div>
                        <div style={cRow}><span style={cK}>{t('HrUsed')}</span><b>{minus(val(b, b.usedDays, b.usedHours))}</b></div>
                        <div style={cRow}><span style={cK}>{t('HrPending')}</span><b>{minus(val(b, b.pendingDays, b.pendingHours))}</b></div>
                        <div style={cTot}><span style={{ fontWeight: 800 }}>{t('HrRemaining')}</span><b style={{ ...cNum, color: (b.remainingDays ?? 0) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>{val(b, b.remainingDays, b.remainingHours)}</b></div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer actions — кнопки одного цвета */}
      <div style={{ display: 'flex', gap: 10, padding: '10px 16px', borderTop: 'var(--border-input)' }}>
        {canAdjust && (
          <button type="button" onClick={() => { setEditing(null); setReturnView('main'); setView('adjust'); }}
            style={{ flex: 1, background: 'var(--btn-user-form)', border: 'var(--border-input)', color: 'var(--text-color)', borderRadius: 9, padding: '9px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            + {t('HrAdjustment')}
          </button>
        )}
        <button type="button" onClick={() => setView('history')}
          style={{ flex: 1, background: 'var(--btn-user-form)', border: 'var(--border-input)', color: 'var(--text-color)', borderRadius: 9, padding: '9px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {t('HrAbsenceHistory')}
          <span style={{ fontSize: 11, fontWeight: 800, background: 'var(--btn-user-in)', color: 'var(--text-color-calendar)', borderRadius: 999, padding: '0 7px' }}>{history.length}</span>
          <span>→</span>
        </button>
      </div>
    </div>
  );
};

export default HrLeaveDetailPanel;
